import { Button } from '@euler/components/Button';
import { Card } from '@euler/components/Card';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ReactNode } from 'react';
import { Pressable, Text, TextInput, View } from 'react-native';
import { useTheme } from 'styled-components';

export function ReviewSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  return (
    <Card title={title} containerStyle={{ marginBottom: rpx(16) }}>
      {description && (
        <Text style={{ color: theme.label.color, marginBottom: rpx(12) }}>
          {description}
        </Text>
      )}
      {children}
    </Card>
  );
}

export function ReviewField({
  label,
  value,
  onChange,
  multiline = false,
  placeholder,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  placeholder?: string;
  disabled?: boolean;
}) {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  return (
    <View style={{ marginBottom: rpx(12) }}>
      <Text style={{ color: theme.form.label.color, marginBottom: rpx(6) }}>
        {label}
      </Text>
      <TextInput
        accessibilityLabel={label}
        value={value}
        onChangeText={onChange}
        multiline={multiline}
        editable={!disabled}
        placeholder={placeholder}
        placeholderTextColor={theme.form.input.placeholderColor}
        autoCorrect={false}
        style={{
          color: theme.form.input.textColor,
          borderColor: theme.form.input.borderColor,
          borderWidth: 1,
          borderRadius: rpx(8),
          padding: rpx(10),
          minHeight: rpx(multiline ? 84 : 44),
          textAlignVertical: 'top',
        }}
      />
    </View>
  );
}

export function ReviewChoice<Value extends string>({
  label,
  value,
  options,
  onChange,
  disabled = false,
}: {
  label: string;
  value: Value;
  options: Array<{ value: Value; label: string }>;
  onChange: (value: Value) => void;
  disabled?: boolean;
}) {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  return (
    <View style={{ marginBottom: rpx(12) }}>
      <Text style={{ color: theme.form.label.color, marginBottom: rpx(8) }}>
        {label}
      </Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {options.map(option => (
          <Pressable
            key={option.value}
            disabled={disabled}
            accessibilityRole="button"
            accessibilityLabel={option.label}
            accessibilityState={{ selected: option.value === value, disabled }}
            onPress={() => onChange(option.value)}
            style={{
              padding: rpx(10),
              marginRight: rpx(6),
              marginBottom: rpx(6),
              borderRadius: 8,
              borderWidth: 1,
              borderColor:
                option.value === value
                  ? theme.colors.primary
                  : theme.form.input.borderColor,
            }}
          >
            <Text
              style={{
                color:
                  option.value === value
                    ? theme.colors.primary
                    : theme.label.color,
              }}
            >
              {option.label}
            </Text>
          </Pressable>
        ))}
      </View>
    </View>
  );
}

export enum ReviewActionTone {
  Primary = 'primary',
  Secondary = 'secondary',
  Destructive = 'destructive',
}

export function ReviewAction({
  title,
  onPress,
  disabled = false,
  loading = false,
  tone = ReviewActionTone.Primary,
}: {
  title: string;
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
  tone?: ReviewActionTone;
}) {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  return (
    <Button
      title={title}
      onPress={onPress}
      disabled={disabled || loading}
      loading={loading}
      style={{
        backgroundColor:
          tone === ReviewActionTone.Primary
            ? theme.colors.primary
            : 'transparent',
        marginTop: rpx(8),
        borderWidth: tone === ReviewActionTone.Primary ? 0 : 1,
        borderColor:
          tone === ReviewActionTone.Destructive
            ? theme.colors.status.danger
            : theme.form.input.borderColor,
      }}
      textStyle={{
        color:
          tone === ReviewActionTone.Primary
            ? theme.actionButton.textColorLight
            : theme.label.color,
      }}
    />
  );
}
